import React, { useContext, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import { Checkbox } from 'ui-components/components/checkbox';
import { INLINE_MESSAGE_TYPES } from 'ui-components/components/inline-message';
import { Input, version2 } from 'ui-components/components/input';
import { Text } from 'ui-components/components/text';
import { TextType } from 'ui-components/components/text/text-type';

import { useAdvancedProductDisplay } from '../../../hooks/use-seller-setting';
import { ProductDetailsContext } from '../context';
import { ProductTypeDropdown } from './product-type-dropdown';
import { messages } from '../messages';
import style from '../style.module.less';

const productNameLabelId = 'product-name-label';
const productSkuLabelId = 'product-sku-label';

export const ProductDetailsProductInfo = () => {
  const {
    productId,
    userHasPermission,
    productName,
    setProductName,
    productSku,
    shouldOverrideImportedName,
    setShouldOverrideImportedName,
    productTypeText,
    setProductSku,
    validationErrors,
    markFieldTouched,
    touchedFields,
    hasAttemptedSave,
  } = useContext(ProductDetailsContext);
  const hasAdvancedProductDisplay = useAdvancedProductDisplay();
  const isNew = !productId;
  const onNameChanged = useCallback(
    (event) => {
      setProductName(event.target.value);
    },
    [setProductName],
  );
  const onSkuChanged = useCallback(
    (event) => {
      setProductSku(event.target.value);
    },
    [setProductSku],
  );
  const onFieldBlur = useCallback(
    (event) => {
      markFieldTouched(event.target.name);
    },
    [markFieldTouched],
  );

  // Calculate conditional error display
  const shouldShowNameError = (touchedFields.includes('productName') || hasAttemptedSave) && validationErrors?.productName;
  const shouldShowSkuError = (touchedFields.includes('productSku') || hasAttemptedSave) && validationErrors?.productSku;

  // Extract error messages and wrap in bold Text component
  const nameErrorText = shouldShowNameError ? (
    <Text isBold textType={TextType.Caption} value={validationErrors.productName?.message} />
  ) : undefined;
  const skuErrorText = shouldShowSkuError ? (
    <Text isBold textType={TextType.Caption} value={validationErrors.productSku?.message} />
  ) : undefined;
  // noinspection JSValidateTypes
  return (
    <>
      <div className={style['name-type-sku']}>
        <div className={style.label} id={productNameLabelId}>
          <FormattedMessage {...messages.productName} />
          <span className={style['required-label']}> &lowast;</span>
        </div>
        <div className={style.label} id={productSkuLabelId}>
          <FormattedMessage {...messages.productSku} />
          {isNew ? <span className={style['required-label']}> &lowast;</span> : null}
        </div>
        <div className={style.label}>
          <FormattedMessage {...messages.productType} />
        </div>
        <div className={style.info}>
          <Input
            aria-labelledby={productNameLabelId}
            data-testid='product-name'
            disabled={!userHasPermission}
            id='product-name-input'
            inlineMessage={nameErrorText}
            inlineMessageType={nameErrorText ? INLINE_MESSAGE_TYPES.error : undefined}
            name='productName'
            onBlur={onFieldBlur}
            onChange={onNameChanged}
            required
            value={productName}
            version={version2}
          />
        </div>
        <div className={style.info}>
          <Input
            aria-labelledby={productSkuLabelId}
            data-testid='product-sku'
            disabled={!isNew}
            id='product-sku-input'
            inlineMessage={skuErrorText}
            inlineMessageType={skuErrorText ? INLINE_MESSAGE_TYPES.error : undefined}
            name='productSku'
            onBlur={onFieldBlur}
            onChange={onSkuChanged}
            required={isNew}
            value={productSku}
            version={version2}
          />
        </div>
        {!isNew && <div className={style['product-type-info']}>{productTypeText}</div>}
        {isNew && hasAdvancedProductDisplay ? <ProductTypeDropdown /> : null}
      </div>
      <div className={style.options}>
        <div className={style.override}>
          <Checkbox
            checked={shouldOverrideImportedName}
            disabled={!userHasPermission}
            label={<FormattedMessage {...messages.overrideImportedNameLabel} />}
            onChange={setShouldOverrideImportedName}
          />
        </div>
      </div>
    </>
  );
};
